import { useEffect, useState } from 'react';
import { Checkbox, Form, Input, Row, Space, Table, message, Modal, Select, Button, Radio } from 'antd';
import 'moment-timezone'
import { AuthenticatedUser } from '../../Types/AuthenticatedUser';

import { TextService } from '../../Services/TextService';
import { useAuth } from '../../Contexts/AuthContext';
import LeftMenuSettings from './LeftSettings';
import { ColumnsType } from 'antd/lib/table';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { WebApiModelsMemberField } from '../../Repository/eventbookyapi';
import { BookySelectDropDown } from '../../Components/Forms/BookySelectDropDown';
import { CustomListService } from '../../Services/CustomListService';
import { UI_CONTROLS } from '../../Types/FieldTypes';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import AddCustomFieldModal from '../MemberSettings/AddCustomFieldModal';
import { CustomField } from '../../Types/CustomField';
import AddFieldModal from '../MemberSettings/AddFieldModal';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { getFieldText } from '../../Helpers/MemberFieldHelper';
import { LookupRelationType } from '../../Types/LookupRelationType';

const MemberSettings = () => {
    const [fields, setFields] = useState([] as (WebApiModelsMemberField | CustomField)[]);
    const [language, setLanguage] = useState("" as string);
    const [customList, setCustomList] = useState<any>(null);
    const [allCustomLists, setAllCustomLists] = useState<any[]>([]);
    const { user } = useAuth();
    const [addFieldModal, setAddFieldModal] = useState(false);
    const [addSystemFieldModal, setAddSystemFieldModal] = useState(false);
    // Removed showUnusedFields, always show only active fields
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    const refreshFields = async () => {
        const updatedFields = await MemberFieldService.GetMemberFields();
        // Sort by columnOrder (or fallback to 0)
        updatedFields.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
        setFields(updatedFields);
    };

    const SaveFields = async () => {
        await MemberFieldService.SaveMemberFields({
            fields: fields.filter((field): field is WebApiModelsMemberField => !!field)
        });

        message.success(GetText("EventBranding_SuccessSaved"));

        // Refresh and sort fields after save
        const updatedFields = await MemberFieldService.GetMemberFields();
        // Sort by columnOrder (or fallback to 0)
        updatedFields.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
        setFields(updatedFields);
    };

    const IsVisibleNotPossibleToChange = (field: WebApiModelsMemberField) => {
        return field.columnType == MemberFieldTypes.FirstName ||
            field.columnType == MemberFieldTypes.LastName ||
            field.columnType == MemberFieldTypes.Address ||
            field.columnType == MemberFieldTypes.PostalArea ||
            field.columnType == MemberFieldTypes.PostalCode ||
            field.columnType == MemberFieldTypes.PhoneNumber ||
            field.columnType == MemberFieldTypes.Email
    }

    const IsVisibleNotPossibleToChangeAdmin = (field: WebApiModelsMemberField) => {

        if (field.columnType == MemberFieldTypes.UniqueId) {
            return true;
        }

        return IsVisibleNotPossibleToChange(field);
    }

    const IsMandatoryNotPossibleToChange = (field: WebApiModelsMemberField) => {
        return field.columnType == MemberFieldTypes.UniqueId
    }

    const HelpTextIdDisabled = (field: WebApiModelsMemberField) => {
        return field.columnType == MemberFieldTypes.UniqueId || field.columnType == MemberFieldTypes.PhoneNumber
    }

    const OnlyExtraFields = (field: WebApiModelsMemberField) => {
        return field.columnType == MemberFieldTypes.Extra1 || field.columnType == MemberFieldTypes.Extra2 || field.columnType == MemberFieldTypes.Extra3
    }

    const updateField = (field: WebApiModelsMemberField) => {
        const newFields = fields.map((element) => {
            // Only update if element is a WebApiModelsMemberField and matches columnType
            if (
                'columnType' in element &&
                'columnType' in field &&
                element.id === field.id
            ) {
                return field;
            }
            return element;
        });
        setFields(newFields);
    }

    const updateVisibleInAdmin = (field: WebApiModelsMemberField, newValue: any) => {
        field.visibleInAdmin = newValue.target.checked;
        updateField(field);
    }

    const updateVisibleInPublic = (field: WebApiModelsMemberField, newValue: any) => {
        field.visiblePublic = newValue.target.checked;
        updateField(field);
    }

    const updateMandatoryAdmin = (field: WebApiModelsMemberField, newValue: any) => {
        field.mandatoryAdmin = newValue.target.checked;
        updateField(field);
    }

    const updatemandatoryPublic = (field: WebApiModelsMemberField, newValue: any) => {
        field.mandatoryPublic = newValue.target.checked;
        updateField(field);
    }

    const updateHelpTextPublic = (field: WebApiModelsMemberField, newValue: any) => {
        field.helpTextPublic = newValue.target.value;
        updateField(field);
    }

    const updateVisibleFieldName = (field: WebApiModelsMemberField, newValue: any) => {
        field.nameAdmin = newValue.target.value;
        field.namePublic = newValue.target.value;
        updateField(field);
    }

    const updateFieldName = (field: WebApiModelsMemberField, newValue: any) => {
        field.nameAdmin = newValue.target.value;
        updateField(field);
    }

    const updateSortOrder = (fieldIndex: number, newValue: any) => {
        const newIndex = newValue - 1;
        if (newIndex < 0 || newIndex >= fields.length || fieldIndex < 0 || fieldIndex >= fields.length) return;

        const reordered = [...fields];
        const [moved] = reordered.splice(fieldIndex, 1);
        reordered.splice(newIndex, 0, moved);

        // Reassign columnOrder to be sequential
        reordered.forEach((f, idx) => { f.columnOrder = idx + 1; });
        setFields(reordered);
    }

    const AvailableSortOrder = Array.from({ length: fields.length }, (_, i) => ({
        label: i + 1,
        value: i + 1,
    }));


    const columns: ColumnsType<WebApiModelsMemberField | CustomField> = [
        {
            title: GetText("MemberFields_FieldName"),
            dataIndex: 'name',
            key: 'name',
            width: "50px",
            render: (_: any, field: WebApiModelsMemberField | CustomField) => {
                if (field.columnType === MemberFieldTypes.Custom) {
                    return (
                        <Input
                            maxLength={100}
                            onChange={(evt: any) => updateVisibleFieldName(field as WebApiModelsMemberField, evt)}
                            value={field.nameAdmin ? field.nameAdmin : ""}
                        />
                    );
                }
                if ('columnType' in field) {
                    return getFieldText(field.columnType!);
                }
                return '';
            },
        },
        {
            title: GetText("MemberFields_FieldSortOrder"),
            dataIndex: 'name',
            key: 'name',
            width: "30px",
            render: (_: any, field: any, index: number) => {
                if ('columnOrder' in field) {
                    return (
                        <BookySelectDropDown
                            style={{ width: "80px" }}
                            handleChange={(evt: any) => {
                                updateSortOrder(index, evt);
                            }}
                            value={field.columnOrder ? field.columnOrder : undefined}
                            options={AvailableSortOrder}
                        />
                    );
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_FieldNameVisible"),
            dataIndex: 'name',
            key: 'name',
            width: "50px",
            render: (_: any, field: any) => {
                if ('nameAdmin' in field) {
                    return <Input maxLength={100} disabled={!OnlyExtraFields(field)} onChange={(evt: any) => updateVisibleFieldName(field, evt)} value={field.nameAdmin ? field.nameAdmin : ""} />;
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_VisibleInAdmin"),
            dataIndex: 'name',
            key: 'name',
            width: "25px",
            render: (_: any, field: any) => {
                if ('visibleInAdmin' in field) {
                    return <Checkbox disabled={IsVisibleNotPossibleToChangeAdmin(field)} onChange={evt => updateVisibleInAdmin(field, evt)} checked={field.visibleInAdmin} />;
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_VisibleInPublic"),
            dataIndex: 'name',
            key: 'name',
            width: "50px",
            render: (_: any, field: any) => {
                if ('visiblePublic' in field) {
                    return <Checkbox disabled={IsVisibleNotPossibleToChange(field)} onChange={evt => updateVisibleInPublic(field, evt)} checked={field.visiblePublic} />;
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_MandatoryInAdmin"),
            dataIndex: 'name',
            key: 'name',
            width: "50px",
            render: (_: any, field: any) => {
                if ('mandatoryAdmin' in field) {
                    return <Checkbox disabled={IsMandatoryNotPossibleToChange(field)} onChange={evt => updateMandatoryAdmin(field, evt)} checked={field.mandatoryAdmin} />;
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_MandatoryInPublic"),
            dataIndex: 'name',
            key: 'name',
            width: "50px",
            render: (_: any, field: any) => {
                if ('mandatoryPublic' in field) {
                    return <Checkbox disabled={IsMandatoryNotPossibleToChange(field)} onChange={evt => updatemandatoryPublic(field, evt)} checked={field.mandatoryPublic} />;
                }
                return null;
            },
        },
        {
            title: GetText("MemberFields_HelpTextPublic"),
            dataIndex: 'helpTextPublic',
            key: 'helpTextPublic',
            width: "200px",
            render: (_: any, field: any) => {
                if ('helpTextPublic' in field) {
                    return <Input disabled={HelpTextIdDisabled(field)} onChange={(evt: any) => updateHelpTextPublic(field, evt)} value={field.helpTextPublic ? field.helpTextPublic : ""} />;
                }
                return null;
            },
        },
        {
            title: 'UI-kontroll',
            dataIndex: 'uiControl',
            key: 'uiControl',
            width: '80px',
            render: (_: any, field: any) => {
                // Only show UI-kontroll dropdown for custom fields
                if (field.columnType === MemberFieldTypes.Custom && 'uiControl' in field) {
                    return (
                        <Select
                            value={field.uiControl}
                            size="small"
                            style={{ width: 110 }}
                            onChange={v => {
                                const updated = fields.map(f => {
                                    if ((f.id && field.id && f.id === field.id) || (f.id && field.fieldId && f.id === field.fieldId)) {
                                        return { ...f, uiControl: v };
                                    }
                                    return f;
                                });
                                setFields(updated);
                            }}
                        >
                            {UI_CONTROLS.map(t => (
                                <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                            ))}
                        </Select>
                    );
                }
                return null;
            },
        },
        {
            title: 'Alternativ',
            dataIndex: 'alternatives',
            key: 'alternatives',
            width: '120px',
            render: (_: any, field: any) => {
                if (field.columnType === MemberFieldTypes.Custom) {
                    return (
                        <Input
                            value={field.alternatives || ''}
                            size="small"
                            onChange={e => {
                                const updated = fields.map(f => {
                                    if ((f.id && field.id && f.id === field.id) || (f.id && field.fieldId && f.id === field.fieldId)) {
                                        return { ...f, alternatives: e.target.value };
                                    }
                                    return f;
                                });
                                setFields(updated);
                            }}
                        />
                    );
                }
                return null;
            },
        },
        {
            title: 'Kopplingstabell',
            dataIndex: 'customListId',
            key: 'customListId',
            width: '120px',
            render: (_: any, field: any) => {
                if (field.columnType === MemberFieldTypes.Custom) {
                    return (
                        <Select
                            value={field.customListId}
                            size="small"
                            style={{ width: 120 }}
                            onChange={v => {
                                const updated = fields.map(f => {
                                    if ((f.id && field.id && f.id === field.id) || (f.id && field.fieldId && f.id === field.fieldId)) {
                                        return { ...f, customListId: v };
                                    }
                                    return f;
                                });
                                setFields(updated);
                            }}
                            allowClear
                            placeholder="Välj lista"
                        >
                            {allCustomLists.map(list => (
                                <Select.Option key={list.id} value={list.id}>{list.name}</Select.Option>
                            ))}
                        </Select>
                    );
                }
                return null;
            },
        },
        {
            title: GetText('MemberFields_LookupRelationType'),
            dataIndex: 'lookupRelationType',
            key: 'lookupRelationType',
            width: '140px',
            render: (_: any, field: any) => {
                // Only show for Custom fields that have a customListId selected
                if (field.columnType === MemberFieldTypes.Custom && field.customListId) {
                    return (
                        <Select
                            value={field.lookupRelationType ?? LookupRelationType.None}
                            size="small"
                            style={{ width: 130 }}
                            onChange={v => {
                                const updated = fields.map(f => {
                                    if ((f.id && field.id && f.id === field.id) || (f.id && field.fieldId && f.id === field.fieldId)) {
                                        return { ...f, lookupRelationType: v };
                                    }
                                    return f;
                                });
                                setFields(updated);
                            }}
                        >
                            <Select.Option value={LookupRelationType.None}>{GetText('MemberFields_LookupRelationType_None')}</Select.Option>
                            <Select.Option value={LookupRelationType.OneToOne}>{GetText('MemberFields_LookupRelationType_OneToOne')}</Select.Option>
                            <Select.Option value={LookupRelationType.OneToMany}>{GetText('MemberFields_LookupRelationType_OneToMany')}</Select.Option>
                            <Select.Option value={LookupRelationType.ManyToMany}>{GetText('MemberFields_LookupRelationType_ManyToMany')}</Select.Option>
                        </Select>
                    );
                }
                return null;
            },
        },
        {
            title: '',
            key: 'remove',
            width: '60px',
            render: (_: any, field: any, index: number) => {
                // Only show remove button if both visibleInAdmin and visiblePublic are not disabled
                const canRemove = !IsVisibleNotPossibleToChangeAdmin(field);
                if (canRemove) {
                    return (
                        <Button danger size="small" onClick={() => {
                            Modal.confirm({
                                title: GetText('MemberFieldSettings_RemoveFieldTitle'),
                                content: GetText('MemberFieldSettings_RemoveFieldConfirm'),
                                okText: GetText('Common_Yes'),
                                cancelText: GetText('Common_Abort'),
                                onOk: () => {
                                    // Simple approach: remove by array index
                                    const updated = [...fields];
                                    updated.splice(index, 1);
                                    // Reassign columnOrder to be sequential
                                    updated.forEach((f, i) => { f.columnOrder = i + 1; });
                                    setFields(updated);
                                },
                            });
                        }}>{GetText('MemberFieldSettings_RemoveField')}</Button>
                    );
                }
                return null;
            },
        },
    ];


    const handleChange = (value: string) => {
        setLanguage(value);
    };

    useEffect(() => {
        if (user) {
            handleChange(user.companyLanguage!);
        }
    }, [user]);

    useEffect(() => {
        MemberFieldService.GetMemberFields().then(result => {
            setFields(result);
        });

        // Load all custom lists and set customList if any has listType === 1
        (async () => {
            try {
                const lists = await CustomListService.GetCustomLists ? await CustomListService.GetCustomLists() : [];
                setAllCustomLists(lists);
                const found = lists.find((l: any) => l.listType === 1);
                if (found) setCustomList(found);
            } catch {
                // ignore error
            }
        })();
    }, []);



    return <LayoutLeftMenu title={GetText("MemberFieldSettings_Title")} menu={<LeftMenuSettings selected={"memberSettings"} />}>
        <Table pagination={false} columns={columns} rowKey="Id" dataSource={[...fields].sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0))} />
        <Space style={{ marginTop: "20px" }}>
            <BookyButton type="primary" onClick={SaveFields} text={GetText("AddMember_Ok")}></BookyButton>
            <BookyButton type="default" onClick={() => setAddSystemFieldModal(true)} text={GetText("MemberFieldSettings_AddField")}></BookyButton>
            <BookyButton disabled={user!.subscriptionLevel < SubscriptionLevel.Premium} type="default" onClick={() => setAddFieldModal(true)} text={GetText("MemberFieldSettings_AddCustomField")}></BookyButton>
            {user!.subscriptionLevel < SubscriptionLevel.Premium && <span><a href='/Pricing'>Uppgradera till premium</a> för att lägga fler fält</span>}
        </Space>
        <AddFieldModal
            open={addSystemFieldModal}
            onCancel={() => setAddSystemFieldModal(false)}
            GetText={GetText}
            currentFields={fields}
            onFieldAdded={async () => {
                await refreshFields();
                setAddSystemFieldModal(false);
            }}
        />
        <AddCustomFieldModal
            open={addFieldModal}
            onCancel={() => setAddFieldModal(false)}
            onFieldAdded={async () => {
                await refreshFields();
                setAddFieldModal(false);
            }}
            allCustomLists={allCustomLists}
            GetText={GetText}
        />
    </LayoutLeftMenu>
};

export default MemberSettings;